
import { Component, Provide, Mixins, Prop } from "vue-property-decorator";
import {
  FilteringSortingTableStore,
  IFilter
} from "@/mixins/tableWithFilteringAndSortingStore";
import { DataModules } from "@/store/modules/data";
import { CreateElement } from "vue";
import {
  IClientTemplatesSlotsData,
  IClientTemplatesSlotsMethods
} from "@/models/providers/templates";
import { mapState } from "vuex";
import { IState } from "@/store";
import { ISorter } from "@/models/tables";
import {
  ITemplate,
  IClientTemplateSlot,
  IClientTemplateSlotMapping,
  IClientTemplateSlotMapped
} from "@/models/templates";
import { ConfirmModalModes } from "@/data/enums";
import { TemplateObjectTypes } from "@/data/enums/templates";
import { DefaultClientAreaTemplateFilters } from "@/data/filters/templates/index";
import { DefaultTemplateSlotsFilters } from "@/data/filters/templates/slots";
import i18n from "@/i18n";
import _ from "@/boot/lodash";
import { DEFAULT_LIST_SCOPE } from "@/store/modules/data";

@Component({
  computed: {
    ...mapState({
      canCreateMapping: (state, getters) =>
        getters[`data/${DataModules.TEMPLATES_CLIENT_MAPPINGS}/canCreate`](),
      canDeleteMapping: (state, getters) =>
        getters[`data/${DataModules.TEMPLATES_CLIENT_MAPPINGS}/canDelete`](),
      withParam(state: IState, getters) {
        return getters[
          `data/${DataModules.TEMPLATES_CLIENT_SLOTS}/withParam`
        ]();
      },
      mappingsWithParam(state: IState, getters) {
        return getters[
          `data/${DataModules.TEMPLATES_CLIENT_MAPPINGS}/withParam`
        ]();
      },
      slotMappings(state: IState, getters) {
        return getters[`data/listArray`]({
          storeModule: DataModules.TEMPLATES_CLIENT_MAPPINGS,
          scope: this.mappingsScope
        });
      }
    })
  }
})
export default class ClientTemplatesSlotsProvider extends Mixins(
  FilteringSortingTableStore
) {
  // Props

  @Prop({ default: "CTSP" /* Client template slots provider */ })
  public declare id: string;

  @Prop({ default: true })
  public declare handleLoading: boolean;

  @Prop({ default: true })
  public declare handleRequestError: boolean;

  @Prop({ default: null })
  public declare templateType: TemplateObjectTypes;

  @Prop({ default: null })
  public declare objectType: TemplateObjectTypes;

  @Prop({ default: null })
  public declare objectId: string;

  @Prop({ default: DEFAULT_LIST_SCOPE })
  public declare mappingsScope: string;

  @Prop({ default: () => ({}) })
  public declare manualFilters: Record<string, string | number | boolean>;

  // Data

  public readonly slotMappings!: IClientTemplateSlotMapping[];
  public readonly withParam!: string;
  public readonly mappingsWithParam!: string;
  public readonly canCreateMapping!: boolean;
  public readonly canDeleteMapping!: boolean;
  public dataModule = DataModules.TEMPLATES_CLIENT_SLOTS;
  public isProcessing = false;
  public isReloading = false;

  public filters: IFilter[] = [
    ...DefaultTemplateSlotsFilters(),
    ...DefaultClientAreaTemplateFilters().map(filter => ({
      ...filter,
      key: `mappings.template.${filter.key}`,
      label: `${i18n.t("_.template")}: ${filter.label}`,
      group: i18n.t("_.template").toString()
    }))
  ];
  public sorters: ISorter[] = [];

  // Computed

  public get slots() {
    return _.map(this.table.data, (slot: IClientTemplateSlot) => {
      const mapping =
        _.find(
          // slot.mappings || [],
          this.slotMappings.filter(i => i.slot_id === slot.id) || [],
          mapping => mapping.object_type === this.objectType
        ) || null;
      return { ..._.omit(slot, ["mappings"]), mapping };
    });
  }

  public get hasSlots(): boolean {
    return !!this.slots.length;
  }

  public get canShowMore(): boolean {
    return this.table.total > this.table.data.length;
  }

  public get isObjectBound() {
    return this.objectId && this.objectType;
  }

  // Life-cycle Methods

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.loading)
      return h("is-loading", { props: { if: true } });
    // Handle request error
    if (this.handleRequestError && this.requestError)
      return h("request-error", {
        props: { isLoading: this.table.loading && this.requestError },
        on: { click: () => this.getData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $slotsData: this.providerData(),
        $slotsMethods: this.providerMethods
      })
    );
  }

  public created() {
    this.loadData();
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binList", {
      storeModule: DataModules.TEMPLATES_CLIENT_MAPPINGS,
      scope: this.mappingsScope,
      vm: this
    });
  }

  // Methods

  public getMappings(config: any) {
    this.$store.dispatch(
      `data/${DataModules.TEMPLATES_CLIENT_MAPPINGS}/list`,
      this.$_.merge(
        {
          vm: this,
          objectType: this.objectType,
          objectId: this.objectId,
          params: {
            with: this.mappingsWithParam,
            ...(!this.isObjectBound ? { "filter[object_id]": "null" } : {})
          }
        },
        config
      )
    );
  }

  public getStoreParams(params: any): Promise<any> {
    return this.$_.merge(
      {
        objectType: this.objectType,
        objectId: this.objectId,
        params: {
          with: this.withParam,
          ...this.manualFilters
        }
      },
      params // Important – must merge last
    );
  }

  public async loadData() {
    const [res] = await Promise.all([
      this.getData({ ignoreStored: this.isReloading }),
      this.getMappings({ ignoreStored: this.isReloading })
    ]);

    return res;
  }

  public async reloadData() {
    try {
      this.isReloading = true;
      this.cancelRequests();
      this.$emit("reloadData");
      return this.loadData();
    } finally {
      this.isReloading = false;
    }
  }

  public openTemplateModal(slot: IClientTemplateSlotMapped) {
    const template = slot.mapping?.template;
    if (!template) return;
    this.$store.dispatch(`data/${DataModules.TEMPLATES}/openAddEditModal`, {
      props: {
        categoryCode: slot.category?.code,
        showCode: false,
        templateId: template?.id,
        templateType: template?.type
      },
      events: {
        /**
         * If template was default, updatedTemplate won't be the same template, but a different one
         * because default templates are duplicated and updatedTemplate.id !== template.id
         */
        success: async ({ id }: ITemplate) => {
          if (template.default) {
            // Replace default with duplicated
            if (slot.mapping?.id) {
              await this.removeMapping(slot.mapping.id);
              await this.createMapping({
                slot_id: slot.id,
                template_id: id
              });
            }
          }
          this.reloadData();
        }
      }
    });
  }

  public openMappingModal(slot: IClientTemplateSlotMapped) {
    if (!this.canCreateMapping) return;
    this.$store.dispatch(
      `data/${DataModules.TEMPLATES_CLIENT_MAPPINGS}/openCreateModal`,
      {
        props: {
          filterTemplateCategoryId: slot.category_id,
          mapping: slot.mapping,
          objectId: this.objectId,
          objectType: this.objectType,
          preselectedSlotId: slot.id
        },
        events: {
          success: () => this.reloadData()
        }
      }
    );
  }

  public async removeTemplateMapping({ mapping }: IClientTemplateSlotMapped) {
    if (!this.canDeleteMapping) return;
    if (!mapping?.id) return;
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          autoClose: false,
          mode: ConfirmModalModes.BUTTON,
          title: this.$t("_action.remove_template_mapping"),
          confirmButtonText: this.$t("_action.remove_mapping"),
          message: this.$t("_sentence.templates.remove_slot_mapping_msg")
        },
        events: {
          confirmed: async () => {
            await this.onDeleteConfirmed(mapping.id);
            confirm.close();
          }
        }
      }
    });
  }

  public async onDeleteConfirmed(
    mappingId: IClientTemplateSlotMapping["id"]
  ): Promise<any> {
    if (!this.canDeleteMapping) return;

    this.table.loading = true;
    try {
      await this.removeMapping(mappingId);
      this.reloadData();
      this.$buefy.toast.open({
        message: this.$t("_.object_successfully_removed", {
          object: this.$t("_.action") as string
        }) as string
      });
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
    }
  }

  public createMapping(data) {
    return this.$store.dispatch(
      `data/${DataModules.TEMPLATES_CLIENT_MAPPINGS}/create`,
      {
        data
      }
    );
  }

  public removeMapping(mappingId: IClientTemplateSlotMapping["id"]) {
    return this.$store.dispatch(
      `data/${DataModules.TEMPLATES_CLIENT_MAPPINGS}/remove`,
      {
        id: mappingId
      }
    );
  }

  // Provide

  @Provide("clientTemplateSlotsProviderMethods")
  public get providerMethods(): IClientTemplatesSlotsMethods {
    return {
      onFiltersChange: this.onFiltersChange,
      onLimitChange: this.onLimitChange,
      onPageChange: this.onPageChange,
      onSort: this.onSort,
      openMappingModal: this.openMappingModal,
      openTemplateModal: this.openTemplateModal,
      reloadData: this.reloadData,
      removeTemplateMapping: this.removeTemplateMapping
    };
  }

  @Provide("clientTemplateSlotsProvider")
  public providerData(): IClientTemplatesSlotsData {
    return {
      constQueryFilters: this.constQueryFilters,
      availableFilters: this.filters,
      availableSorters: this.sorters,
      activeFilters: this.activeFilters,
      activeSort: this.table.sort,
      canShowMore: this.canShowMore,
      enableFiltering: this.enableFiltering,
      enableSorting: this.enableSorting,
      hasSlots: this.hasSlots,
      isLoading: this.table.loading,
      limit: this.table.limit,
      page: this.table.page,
      slots: this.slots,
      table: this.table,
      total: this.table.total,
      canCreateMapping: this.canCreateMapping,
      canDeleteMapping: this.canDeleteMapping
    };
  }
}
